import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AlertController, IonBackButton, IonContent, IonHeader, IonItem, IonLabel, IonList, IonPopover, IonTitle, IonToolbar, LoadingController, ModalController } from '@ionic/angular/standalone';
import { Store } from "@ngrx/store";
import { OrganisationUserRoles } from "@qid/core";
import { SexyModule } from "@qid/sexy";
import { AvatarRoundComponent, SuperButtonComponent } from "@qid/super";
import { HideForAppDirective } from '../../../../directives/hide-for-app.directive';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { activeAccountChanges, AuthSelectors } from "../../../authentication";
import { Organisation, OrganisationService } from '../../../organisation';
import { OrganisationUserActions, OrganisationUserSelectors } from "../../../organisation-user";
import { CreateOrganisationRelationshipDto } from "../../dto/create-organisation-relationship.dto";
import { OrganisationRelationship } from "../../model/organisation-relationship.model";
import { OrganisationRelationshipFacade } from "../../services/organisation-relationship.facade";
import { OrganisationRelationshipService } from "../../services/organisation-relationship.service";
import {
  EditOrganisationRelationshipPage
} from "../edit-organisation-relationship/edit-organisation-relationship-page.component";
import { AddBranchPage } from '../add-branch/add-branch.page';

@Component({
  selector: 'app-list-child-orgs',
  templateUrl: './list-child-orgs.page.html',
  styleUrls: ['./list-child-orgs.page.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SuperButtonComponent,
    AvatarRoundComponent,
    HideForAppDirective,
    SexyModule,
    IonHeader,
    IonToolbar,
    IonBackButton,
    IonTitle,
    IonContent,
    IonPopover,
    IonList,
    IonItem,
    IonLabel
  ]
})
export class ListChildOrgsPage implements OnInit {

  public activeAccount$ = this.store.select(AuthSelectors.selectActiveAccount);

  public organisationRelationships$ = this.orgRelationshipsFacade.childOrganisations$;
  public parentOrganisation$ = this.orgRelationshipsFacade.parentBranchRealation$;

  public organisationsThatCanBeAdded: Organisation[] = null

  private pageClosed$ = new Subject<void>();

  constructor(private modalController: ModalController,
    private store: Store,
    private router: Router,
    private organisationRelationshipService: OrganisationRelationshipService,
    private orgRelationshipsFacade: OrganisationRelationshipFacade,
    private loadingController: LoadingController,
    private alertController: AlertController,
    private organisationService: OrganisationService,
  ) {

  }

  ngOnInit() {
  }

  ionViewDidEnter() {
    this.pageClosed$ = new Subject<void>();
    activeAccountChanges(this.store)
      .pipe(takeUntil(this.pageClosed$))
      .subscribe(() => this.orgRelationshipsFacade.initParentBranch());
  }

  ionViewDidLeave() {
    this.pageClosed$.next();
    this.pageClosed$.complete();
  }


  openBranchBilling = () => {
    this.router.navigateByUrl('/menu/settings/branch-billing');
  };

  openOrgSelector = async () => {

    const modal = await this.modalController.create({
      component: AddBranchPage,
    })
    modal.present();


  }

  onCreateOrgClicked = async () => {

    const alert = await this.alertController.create({
      header: 'Create Organisation',
      message: 'Please enter the name of the organisation',
      inputs: [
        {
          name: 'text',
          type: 'text',
          placeholder: 'Organisation Name',
        }
      ],
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel'
        },
        {
          text: 'Create',
          handler: async (data) => {
            const loader = await this.loadingController.create({
              message: 'Creating Account'
            })
            loader.present()
            const parentOrg: OrganisationRelationship = await firstValueFrom(this.parentOrganisation$)
            const org = await this.organisationService.create({
              name: data.text,
              industry: parentOrg?.parentOrg?.industry?._id,
            })
            loader.dismiss()
            if (org) {
              this.store.dispatch(OrganisationUserActions.fetchUserOrganisations())
              await this.setParentOrganisation(org._id, null)
            }
          }
        }
      ]
    })
    alert.present();
  }

  async setParentOrganisation(childOrgId: string, index: number) {

    const loader = await this.loadingController.create({
      message: 'Adding Branch'
    })

    loader.present();

    const activeAccount: Organisation = await firstValueFrom(this.store.select(AuthSelectors.selectActiveAccount))

    const dto: CreateOrganisationRelationshipDto = {
      parentOrg: activeAccount._id,
      childOrg: childOrgId
    }
    const res = await this.organisationRelationshipService.create(dto)

    loader.dismiss();

    if (res && res.data) {
      index ? this.organisationsThatCanBeAdded.splice(index, 1) : null;
      this.orgRelationshipsFacade.refresh()
    }

  }

  async openOrgRelationEdit(orgRelationship: OrganisationRelationship) {
    const modal = await this.modalController.create({
      component: EditOrganisationRelationshipPage,
      componentProps: {
        orgRelationship: { ...orgRelationship }
      }
    })
    modal.present();
    const res = await modal.onDidDismiss()

    if (res?.data?.deleted == true) {
      this.orgRelationshipsFacade.refresh()
    }
  }

  async getOrganisations(): Promise<Organisation[]> {
    const state = await firstValueFrom(
      this.store.select(OrganisationUserSelectors.selectOrganisationUserState)
    );

    if (!state?.authenticatedUserOrganisations?.length) {
      return [];
    }

    const activeAccount = await firstValueFrom(this.activeAccount$)

    const allOrganisations = state.authenticatedUserOrganisations
      .filter(({ role, organisation }) =>
        (role >= OrganisationUserRoles.ADMIN && organisation._id !== activeAccount._id))

      .map(({ organisation }) => organisation);

    if (!allOrganisations.length) {
      return [];
    }

    const organisationRelationships = await firstValueFrom(this.organisationRelationships$);

    if (!organisationRelationships?.length) {
      return allOrganisations;
    }

    const alreadyAddedOrganisations = new Set(
      organisationRelationships.map(({ childOrg }) => childOrg._id)
    );

    const uniqueOrganisations = allOrganisations.filter(
      ({ _id }) => !alreadyAddedOrganisations.has(_id)
    );

    return uniqueOrganisations;
  }


}
